/* eslint no-restricted-syntax: 0, no-await-in-loop: 0 */
import {
  DirectoryGroupingConsts,
  ItemTypeConsts,
} from 'client/util/Constants';
import { cloneDeep, get } from 'lodash';
import PolicyApi from 'client/api/v2/PolicyApi';
import PolicyTemplateApi from 'client/api/v2/PolicyTemplateApi';
import PolicyUtil from 'client/policies/PolicyUtil';
import createModel from 'client/store/modules/models/createModel';
import hashString from 'client/GrowthEngineering/utilities/hashString';

const { groupTypes, memberTypes } = DirectoryGroupingConsts;

export const getDefaultItem = () => ({
  id: '',
  name: '',
  templateId: '',
  type: ItemTypeConsts.configuration,
});

const model = createModel(getDefaultItem, 'id', PolicyApi);

// Helper function

export const getConfigFieldWithValue = (
  browserPatchMgmt,
  configFieldID,
  os,
  policy,
  policiesConfigFields,
) => {
  let configField = {};
  Object.entries(policy).forEach(([updateType, fields]) => {
    if (updateType === 'name') return; // Ignore the 'name' property
    Object.entries(fields).forEach(([field, value]) => {
      if (browserPatchMgmt) {
        if (configFieldID === policiesConfigFields[os][updateType]) {
          configField = {
            configFieldID,
            value,
          };
        }
      } else if (configFieldID === policiesConfigFields[os][updateType][field]) {
        configField = {
          configFieldID,
          value,
        };
      }
    });
  });

  return configField;
};

export function hashLogo(base64LogoString) {
  let logoData = base64LogoString;
  const pos = logoData.indexOf(',');
  if (pos > 0) {
    logoData = logoData.substring(pos + 1);
  }
  const logoBytes = Buffer.from(logoData, 'base64');
  return hashString(logoBytes);
}

export default {
  namespaced: true,

  state: {
    ...model.state,
  },

  actions: {
    ...model.actions,

    bindConfigurationToSystems(context) {
      return context.dispatch('bindAssociations', {
        parentType: memberTypes.policy,
        targetType: memberTypes.system,
      });
    },

    bindConfigurationToSystemGroups(context) {
      return context.dispatch('bindAssociations', {
        parentType: memberTypes.policy,
        targetType: groupTypes.systemGroup,
      });
    },

    bindConfigurationToConfigurationGroups(context) {
      return context.dispatch('bindAssociations', {
        parentType: memberTypes.policy,
        targetType: groupTypes.policyGroup,
      });
    },

    async bulkCreatePatchPolicies(context, templatesData) {
      const { dispatch, getters } = context;
      const {
        browserPatchMgmt = false, policiesConfigFields, templates, suffix, zeroStateData,
      } = templatesData;
      const createTasks = [];
      const zeroStateTemplates = {};

      Object.entries(zeroStateData)
        .forEach(([item, template]) => {
          if (browserPatchMgmt) {
            if (templates.find(({ name }) => name.includes(item))) {
              zeroStateTemplates[item] = template;
            }
          }
          if (templates.find(({ osMetaFamily }) => osMetaFamily === item)) {
            zeroStateTemplates[item] = template;
          }
        });

      for (const policyType of Object.keys(zeroStateTemplates)) {
        const index = browserPatchMgmt
          ? templates.findIndex(({ name }) => name.includes(policyType))
          : templates.findIndex(({ osMetaFamily }) => osMetaFamily === policyType);

        dispatch('setItemField', {
          key: 'templateId',
          value: templates[index].id,
        });
        await dispatch('fetchItem');

        zeroStateData[policyType].forEach(policy => {
          const template = getters.item;
          const itemToSave = {
            ...template,
            id: '',
            name: suffix ? policy.name + suffix : policy.name,
            values: cloneDeep(template.values.map(configField => {
              const params = [
                browserPatchMgmt,
                configField.configFieldID,
                policyType,
                policy,
                policiesConfigFields,
              ];
              const configFieldWithValue = getConfigFieldWithValue(...params);

              if (Object.keys(configFieldWithValue).length > 0) {
                return configFieldWithValue;
              }
              return configField;
            })),
          };

          createTasks.push(PolicyApi.create(itemToSave));
        });
      }

      return Promise.all(createTasks);
    },

    fetchItem(context = { getters: { id: {} } }, customTemplateId = null) {
      const { id } = context.getters;
      const { templateId } = context.state.item;
      const getSectionsData = (configFields) => {
        const sections = [];
        const nonSectionFields = [];
        const seenSections = new Map();
        configFields.forEach((field) => {
          const section = get(field, 'displayOptions.group.section');
          if (!section) {
            nonSectionFields.push(field);
          } else if (!seenSections.has(section.id)) {
            sections.push({ ...section });
            seenSections.set(section.id, section);
          }
        });
        return { sections, nonSectionFields };
      };

      if (templateId || customTemplateId) {
        const temId = customTemplateId || templateId;
        return PolicyTemplateApi
          .get(temId)
          .then(async (item) => {
            const name = item.displayName;
            // sort config fields by position number
            item.configFields.sort(
              (fieldA, fieldB) => fieldA.position - fieldB.position,
            );
            const { sections, nonSectionFields } = getSectionsData(item.configFields);
            const values = item.configFields
              .map(field => ({
                configFieldID: field.id,
                configFieldName: field.name,
                value: field.defaultValue,
              }));

            const templateObj = {
              template: item, name, values, sections, nonSectionFields,
            };

            if (!customTemplateId) {
              context.commit('SET_ITEM', templateObj);
              context.commit('SET_PREVIOUS_ITEM', templateObj);
            } else {
              return templateObj;
            }
            return item;
          });
      }

      if (id) {
        return PolicyApi
          .get(id)
          .then(async (item) => {
            // Clone the item so that any changes to non-primitive type values,
            // such as an array of phone numbers, are _not_ accidentally made
            // to the previous item when the item is mutated.
            item.template.configFields.sort(
              (fieldA, fieldB) => fieldA.position - fieldB.position,
            );
            const { sections, nonSectionFields } = getSectionsData(item.template.configFields);
            // if a policy template is updated the item values array might have incomplete data
            // we want to fill out any new template fields with default values
            // so they are saved with all the updated values from the template
            const values = item.template.configFields
              .map((field) => {
                const valueIndex = item.values.findIndex(value => value.configFieldID === field.id);
                const defaultField = { configFieldID: field.id, value: field.defaultValue };

                return valueIndex >= 0 ? item.values[valueIndex] : defaultField;
              });

            const updatedValuesItem = {
              template: item.template,
              name: item.name,
              values,
              sections,
              nonSectionFields,
            };
            context.commit('SET_ITEM', cloneDeep(updatedValuesItem));
            context.commit('SET_PREVIOUS_ITEM', cloneDeep(updatedValuesItem));

            return item;
          });
      }

      return Promise.resolve();
    },
  },

  getters: {
    ...model.getters,

    displayName(state) {
      return state.item.name;
    },

    requiresSupervision(state) {
      return PolicyUtil.requiresSupervision(state.item);
    },
  },

  mutations: {
    ...model.mutations,
  },
};
